/**
 * Accountant dashboard top-level shell.
 *
 * The L1 hand-rolled tab switcher is gone — we now use React Router so every
 * screen has a real URL path. That unlocks:
 *   - back/forward browser navigation
 *   - linkable views (operator-cli + dashboard URLs become greppable in audit)
 *   - feature-specific routes like /clients/:id, /2fa/setup, /settings/agent
 *
 * Authentication remains bearer-token in localStorage (api.ts owns it). On
 * each render this component checks for a valid session via /auth/me; an
 * unauthenticated user is redirected to /login. The /login screen and the
 * /2fa/* setup screens are public-shell variants — they render without the
 * AppShell + sidebar.
 */

import { AppShell, Burger, Container, Group, NavLink, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { t } from "@dovach/i18n";

import { AuditScreen } from "./screens/AuditScreen";
import { CertViewScreen } from "./screens/CertViewScreen";
import { ForgotPasswordScreen } from "./screens/ForgotPasswordScreen";
import { ResetPasswordScreen } from "./screens/ResetPasswordScreen";
import { SignupScreen } from "./screens/SignupScreen";
import { VerifyEmailScreen } from "./screens/VerifyEmailScreen";
import { ClientCollectionView } from "./screens/ClientCollectionView";
import { ClientDetailScreen } from "./screens/ClientDetailScreen";
import { ClientsScreen } from "./screens/ClientsScreen";
import { DocumentTemplatesScreen } from "./screens/DocumentTemplatesScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { InboxScreen } from "./screens/InboxScreen";
import { KeypairWizardScreen } from "./screens/KeypairWizardScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { RunMatrixScreen } from "./screens/RunMatrixScreen";
import { SecuritySettingsScreen } from "./screens/SecuritySettingsScreen";
import { SettingsAgentScreen } from "./screens/SettingsAgentScreen";
import { TwoFARecoveryScreen } from "./screens/TwoFARecoveryScreen";
import { TwoFASetupScreen } from "./screens/TwoFASetupScreen";
import { TwoFAVerifyScreen } from "./screens/TwoFAVerifyScreen";
import { startPullLoop, stopPullLoop } from "./lib/pull-loop";
import { isUnlocked } from "./lib/session-key";
import { api, auth, type MeResponse } from "./lib/api";

const DEV_BYPASS_AUTH =
  import.meta.env.DEV &&
  (import.meta as { env?: { VITE_DEV_BYPASS_AUTH?: string } }).env?.VITE_DEV_BYPASS_AUTH === "true";

const DEV_FAKE_ME: MeResponse = {
  account_id: "dev",
  email: "dev@dovach.local",
  display_name: "Dev User",
  has_2fa: false,
};

export function App() {
  const [me, setMe] = useState<MeResponse | null | undefined>(
    DEV_BYPASS_AUTH ? DEV_FAKE_ME : undefined,
  );

  useEffect(() => {
    if (DEV_BYPASS_AUTH) return;
    if (!auth.getToken()) {
      setMe(null);
      return;
    }
    api
      .me()
      .then(setMe)
      .catch(() => {
        // After the api.ts auto-refresh attempt, a final 401 means both
        // access AND refresh tokens are unusable — clear both.
        auth.setTokens(null);
        setMe(null);
      });
  }, []);

  if (me === undefined) {
    return <Container py="lg">{t("common.loading")}</Container>;
  }

  return (
    <Routes>
      {/* Public shell — no AppShell, no nav. */}
      <Route
        path="/login"
        element={
          me === null ? (
            <LoginScreen onLoggedIn={(profile) => setMe(profile)} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/2fa/verify" element={<TwoFAVerifyScreen onLoggedIn={(profile) => setMe(profile)} />} />
      <Route path="/2fa/recovery" element={<TwoFARecoveryScreen onLoggedIn={(profile) => setMe(profile)} />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/signup" element={<SignupScreen />} />
      <Route path="/verify-email" element={<VerifyEmailScreen />} />

      {/* Authenticated shell — every route below requires a session. */}
      <Route element={<AuthedShell me={me} onLogout={() => setMe(null)} />}>
        <Route path="/setup" element={<KeypairWizardScreen onReady={() => { /* nav back via Link */ }} />} />
        <Route path="/" element={<HomeScreen />} />
        <Route path="/clients" element={<ClientsScreen />} />
        <Route path="/clients/:id" element={<ClientDetailScreen />} />
        <Route path="/document-templates" element={<DocumentTemplatesScreen />} />
        <Route path="/campaigns" element={<Navigate to="/" replace />} />
        <Route path="/runs" element={<Navigate to="/" replace />} />
        <Route path="/runs/:id" element={<RunMatrixScreen />} />
        <Route
          path="/runs/:runId/clients/:clientId"
          element={<ClientCollectionView />}
        />
        <Route path="/run-kickoff" element={<Navigate to="/runs" replace />} />
        <Route path="/inbox" element={<InboxScreen />} />
        <Route path="/audit" element={<AuditScreen />} />
        <Route path="/settings/agent" element={<SettingsAgentScreen />} />
        <Route path="/settings/security" element={<SecuritySettingsScreen />} />
        <Route path="/security/cert" element={<CertViewScreen />} />
        <Route path="/2fa/setup" element={<TwoFASetupScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * AuthedShell wraps the outlet with the AppShell + sidebar. If the user
 * isn't logged in, it redirects to /login while preserving where they
 * were trying to go.
 */
function AuthedShell({ me, onLogout }: { me: MeResponse | null; onLogout: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Start the browser-side pull loop once the user has unlocked O_sk.
  // Stop on logout or tab unmount.
  useEffect(() => {
    if (me !== null && isUnlocked()) {
      startPullLoop({ enabled: () => isUnlocked() });
      return () => stopPullLoop();
    }
    return undefined;
  }, [me]);

  if (me === null) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const mainNavItems = [
    { to: "/", label: "ניהול קמפיינים" },
    { to: "/inbox", label: "תיבת הקלט" },
    { to: "/clients", label: "לקוחות" },
  ];

  const topSettingsItems = [
    { to: "/document-templates", label: "תבניות מסמכים" },
    { to: "/settings/agent", label: "הורדת קבצים" },
    { to: "/audit", label: "יומן ביקורת" },
  ];

  const securityItems = [
    { to: "/setup", label: "מפתח הצפנה" },
    { to: "/security/cert", label: "תעודת הזיהוי שלי" },
    { to: "/settings/security", label: "אבטחה" },
    { to: "/2fa/setup", label: "הגדרת 2FA" },
  ];

  const allSettingsItems = [...topSettingsItems, ...securityItems];

  const isInSettings = allSettingsItems.some((item) =>
    location.pathname.startsWith(item.to),
  );

  const isInSecurity = securityItems.some((item) =>
    location.pathname.startsWith(item.to),
  );

  const [settingsOpen, setSettingsOpen] = useState(isInSettings);
  const [securityOpen, setSecurityOpen] = useState(isInSecurity);
  const [navbarOpen, setNavbarOpen] = useState(true);

  return (
    <AppShell
      padding="md"
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { desktop: !navbarOpen, mobile: !navbarOpen } }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={navbarOpen} onClick={() => setNavbarOpen((o) => !o)} size="sm" />
            <Text fw={700} size="lg" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>Dovach</Text>
          </Group>
          <Group>
            <Text size="sm">{me.display_name}</Text>
            <Text
              size="sm"
              c="blue"
              style={{ cursor: "pointer" }}
              onClick={() => {
                // Fire-and-forget the server-side revoke so the session
                // family is killed across all devices; don't block the
                // local navigation on it.
                api.logout();
                auth.setTokens(null);
                onLogout();
                navigate("/login", { replace: true });
              }}
            >
              התנתק
            </Text>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <Stack gap={4}>
          {mainNavItems.map((item) => (
            <NavLink
              key={item.to}
              active={
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to)
              }
              label={item.label}
              onClick={() => navigate(item.to)}
            />
          ))}
          <NavLink
            label="הגדרות"
            active={isInSettings}
            opened={settingsOpen}
            onChange={setSettingsOpen}
            childrenOffset={12}
            disableRightSectionRotation
            rightSection={
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ transition: "transform 200ms ease", transform: settingsOpen ? "rotate(90deg)" : "rotate(180deg)" }}>
                <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          >
            {topSettingsItems.map((item) => (
              <NavLink
                key={item.to}
                active={location.pathname.startsWith(item.to)}
                label={item.label}
                onClick={() => navigate(item.to)}
              />
            ))}
            <NavLink
              label="אבטחה"
              active={isInSecurity}
              opened={securityOpen}
              onChange={setSecurityOpen}
              childrenOffset={12}
              disableRightSectionRotation
              rightSection={
                <svg width="12" height="12" viewBox="0 0 12 12" style={{ transition: "transform 200ms ease", transform: securityOpen ? "rotate(90deg)" : "rotate(180deg)" }}>
                  <path d="M4.5 2L8.5 6L4.5 10" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            >
              {securityItems.map((item) => (
                <NavLink
                  key={item.to}
                  active={location.pathname.startsWith(item.to)}
                  label={item.label}
                  onClick={() => navigate(item.to)}
                />
              ))}
            </NavLink>
          </NavLink>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Container size="lg">
          <Outlet />
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
